import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, Trash2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CreatePayslip = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  const [formData, setFormData] = useState({
    employee_id: '',
    employee_name: '',
    position: '',
    pay_period: '',
    pay_date: new Date().toISOString().split('T')[0],
    basic_salary: 0,
    overtime_hours: 0,
    overtime_rate: 0,
    cpf_employee: 0,
    cpf_employer: 0,
    allowances: [],
    bonuses: [],
    other_deductions: []
  });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/payslips/employees`);
      setEmployees(res.data);
    } catch (error) {
      toast.error('Failed to load employees');
    }
  };

  const handleEmployeeSelect = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    if (employee) {
      setSelectedEmployee(employee);
      setFormData({
        ...formData,
        employee_id: employee.id,
        employee_name: employee.name,
        position: employee.position,
        basic_salary: employee.basic_salary || 0
      });
    }
  };

  const addItem = (type) => {
    setFormData({
      ...formData,
      [type]: [...formData[type], { description: '', amount: 0 }]
    });
  };

  const removeItem = (type, index) => {
    setFormData({
      ...formData,
      [type]: formData[type].filter((_, i) => i !== index)
    });
  };

  const updateItem = (type, index, field, value) => {
    const updated = [...formData[type]];
    updated[index][field] = field === 'amount' ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, [type]: updated });
  };

  const calculateTotals = () => {
    const overtimePay = formData.overtime_hours * formData.overtime_rate;
    const allowancesTotal = formData.allowances.reduce((sum, a) => sum + (a.amount || 0), 0);
    const bonusesTotal = formData.bonuses.reduce((sum, b) => sum + (b.amount || 0), 0);
    const grossPay = formData.basic_salary + overtimePay + allowancesTotal + bonusesTotal;
    
    const deductionsTotal = formData.other_deductions.reduce((sum, d) => sum + (d.amount || 0), 0);
    const totalDeductions = formData.cpf_employee + deductionsTotal;
    
    const netPay = grossPay - totalDeductions;
    
    return { overtimePay, grossPay, totalDeductions, netPay };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.employee_id) {
      toast.error('Please select an employee');
      return;
    }
    if (!formData.pay_period) {
      toast.error('Please enter pay period');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/payslips/`, formData);
      toast.success('Payslip created successfully');
      navigate('/admin/payslip');
    } catch (error) {
      toast.error('Failed to create payslip');
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: 'SGD'
    }).format(amount || 0);
  };

  return (
    <div className="min-h-screen pt-44 pb-12 px-4 bg-gray-50">
      <div className="container mx-auto max-w-4xl">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="outline" onClick={() => navigate('/admin/payslip')}>
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Create Payslip</h1>
            <p className="text-gray-600">Generate a new employee payslip</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Employee Selection */}
          <Card>
            <CardHeader>
              <CardTitle>Employee Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Select Employee *</Label>
                <select
                  data-testid="employee-select-dropdown"
                  className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white"
                  value={formData.employee_id}
                  onChange={(e) => handleEmployeeSelect(e.target.value)}
                  required
                >
                  <option value="">Choose an employee...</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name} - {emp.position}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Pay Period *</Label>
                  <Input
                    placeholder="e.g., March 2026"
                    value={formData.pay_period}
                    onChange={(e) => setFormData({...formData, pay_period: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Pay Date *</Label>
                  <Input
                    type="date"
                    value={formData.pay_date}
                    onChange={(e) => setFormData({...formData, pay_date: e.target.value})}
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Earnings */}
          <Card>
            <CardHeader>
              <CardTitle>Earnings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Basic Salary</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.basic_salary}
                    onChange={(e) => setFormData({...formData, basic_salary: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>OT Hours</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={formData.overtime_hours}
                      onChange={(e) => setFormData({...formData, overtime_hours: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>OT Rate/Hr</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.overtime_rate}
                      onChange={(e) => setFormData({...formData, overtime_rate: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </div>
              </div>

              {/* Allowances */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Allowances</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => addItem('allowances')}>
                    <Plus size={14} className="mr-1" /> Add
                  </Button>
                </div>
                {formData.allowances.map((item, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateItem('allowances', index, 'description', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Amount"
                      value={item.amount}
                      onChange={(e) => updateItem('allowances', index, 'amount', e.target.value)}
                      className="w-32"
                    />
                    <Button type="button" variant="outline" size="icon" onClick={() => removeItem('allowances', index)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Bonuses */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Bonuses</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => addItem('bonuses')}>
                    <Plus size={14} className="mr-1" /> Add
                  </Button>
                </div>
                {formData.bonuses.map((item, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateItem('bonuses', index, 'description', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Amount"
                      value={item.amount}
                      onChange={(e) => updateItem('bonuses', index, 'amount', e.target.value)}
                      className="w-32"
                    />
                    <Button type="button" variant="outline" size="icon" onClick={() => removeItem('bonuses', index)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Deductions */}
          <Card>
            <CardHeader>
              <CardTitle>Deductions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>CPF Employee Contribution</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.cpf_employee}
                    onChange={(e) => setFormData({...formData, cpf_employee: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>CPF Employer Contribution</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.cpf_employer}
                    onChange={(e) => setFormData({...formData, cpf_employer: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>

              {/* Other Deductions */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Other Deductions</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => addItem('other_deductions')}>
                    <Plus size={14} className="mr-1" /> Add
                  </Button>
                </div>
                {formData.other_deductions.map((item, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateItem('other_deductions', index, 'description', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Amount"
                      value={item.amount}
                      onChange={(e) => updateItem('other_deductions', index, 'amount', e.target.value)}
                      className="w-32"
                    />
                    <Button type="button" variant="outline" size="icon" onClick={() => removeItem('other_deductions', index)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Basic Salary</span>
                  <span>{formatCurrency(formData.basic_salary)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Overtime Pay ({formData.overtime_hours} hrs × {formatCurrency(formData.overtime_rate)})</span>
                  <span>{formatCurrency(totals.overtimePay)}</span>
                </div>
                <div className="flex justify-between font-semibold border-t pt-2">
                  <span>Gross Pay</span>
                  <span>{formatCurrency(totals.grossPay)}</span>
                </div>
                <div className="flex justify-between text-red-600">
                  <span>Total Deductions</span>
                  <span>-{formatCurrency(totals.totalDeductions)}</span>
                </div>
                <div className="flex justify-between text-xl font-bold border-t pt-2 text-green-600">
                  <span>Net Pay</span>
                  <span>{formatCurrency(totals.netPay)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Button data-testid="create-payslip-submit" type="submit" className="w-full" disabled={loading}>
            {loading ? 'Creating...' : 'Create Payslip'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CreatePayslip;
